/**
 * manager-splide.js
 * Inicializa instâncias do Splide a partir de data attributes
 * e sincroniza o autoplay de vídeos por slide.
 */

class SplideManager {
  constructor() {
    this.selector = '[data-splide-component]';
    this.retryDelay = 80;
    this.retryLimit = 40;
    this._videoObservers = new WeakMap();
    this._mountObservers = new WeakMap();

    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
    this._handleBlockSelect = this._handleBlockSelect.bind(this);

    this._bindEvents();
    this._boot();
  }

  _boot() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
      return;
    }

    this.init();
  }

  _bindEvents() {
    document.addEventListener('shopify:section:load', this._handleSectionLoad);
    document.addEventListener('shopify:section:unload', this._handleSectionUnload);
    document.addEventListener('shopify:block:select', this._handleBlockSelect);
  }

  _handleSectionLoad(event) {
    this.init(event.target);
  }

  _handleSectionUnload(event) {
    this.destroy(event.target);
  }

  _handleBlockSelect(event) {
    const blockId = event.detail?.blockId;
    const slide = blockId
      ? document.querySelector(`[data-splide-block-id="${blockId}"]`)
      : event.target?.closest?.('[data-splide-slide-index]');
    if (!slide) return;

    const root = slide.closest(this.selector);
    if (!root?.splide) return;

    const index = Number(slide.dataset.splideSlideIndex || 0);
    root.splide.go(index);
    this._syncSlideVideos(root.splide);
  }

  init(scope = document, attempt = 0) {
    const roots = scope.matches?.(this.selector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(this.selector) || []);

    if (!roots.length) return;

    if (typeof window.Splide === 'undefined') {
      if (attempt >= this.retryLimit) return;
      window.setTimeout(() => this.init(scope, attempt + 1), this.retryDelay);
      return;
    }

    roots.forEach((root) => this._mountWhenReady(root));
  }

  destroy(scope = document) {
    const roots = scope.matches?.(this.selector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(this.selector) || []);

    roots.forEach((root) => {
      const videoObserver = this._videoObservers.get(root);
      if (videoObserver) {
        videoObserver.disconnect();
        this._videoObservers.delete(root);
      }

      const mountObserver = this._mountObservers.get(root);
      if (mountObserver) {
        mountObserver.disconnect();
        this._mountObservers.delete(root);
      }

      if (!root.splide) return;
      root.splide.destroy();
      delete root.splide;
    });
  }

  _mountWhenReady(root) {
    if (!root) return;

    const mount = () => {
      if (root.dataset.splideComponent === 'product-slider' && typeof window.loadProductManager === 'function') {
        const productManagerLoad = window.loadProductManager();
        if (productManagerLoad && typeof productManagerLoad.then === 'function') {
          productManagerLoad.catch(() => {}).then(() => this._mount(root));
          return;
        }
      }

      this._mount(root);
    };
    if (window.Shopify && window.Shopify.designMode) {
      this._disconnectMountObserver(root);
      mount();
      return;
    }

    const shouldDeferMount = root.dataset.splideDeferMount === 'true';
    const canObserve = typeof window.IntersectionObserver !== 'undefined';

    if (!shouldDeferMount || !canObserve || this._isInViewport(root)) {
      this._disconnectMountObserver(root);
      mount();
      return;
    }

    if (this._mountObservers.has(root)) return;

    const rootMargin = root.dataset.splideDeferRootMargin || '600px 0px';
    const observer = new window.IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      this._disconnectMountObserver(root);
      mount();
    }, { rootMargin });

    this._mountObservers.set(root, observer);
    observer.observe(root);
  }

  _disconnectMountObserver(root) {
    const observer = this._mountObservers.get(root);
    if (!observer) return;

    observer.disconnect();
    this._mountObservers.delete(root);
  }
  _mount(root) {
    if (!root) return;

    const slides = root.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
    if (!slides.length) return;

    if (root.splide) {
      root.splide.destroy();
      delete root.splide;
    }

    const options = this._getOptions(root);
    const instance = new window.Splide(root, options);

    instance.on('mounted moved resized refresh updated', () => {
      this._updateCounter(instance);
      this._updateSlideAccessibility(instance);
      this._syncSlideVideos(instance);
    });

    instance.on('destroy', () => {
      this._pauseAllVideos(root);
    });

    instance.mount();
    root.splide = instance;

    if (options.autoplay === 'pause') {
      const startAutoplay = () => instance.Components.Autoplay?.play();
      if (document.readyState === 'complete') {
        startAutoplay();
      } else {
        window.addEventListener('load', startAutoplay, { once: true });
      }
    }

    this._observeVideos(instance);
    this._syncSlideVideos(instance);
  }

  _getOptions(root) {
    const counterType = root.dataset.splideCounterType || 'dots';
    const arrows = this._toBoolean(root.dataset.splideArrows, true);
    const showArrowsMobile = this._toBoolean(root.dataset.splideArrowsMobile, false);
    let autoplay = this._toBoolean(root.dataset.splideAutoplay, true);
    if (root.dataset.splideComponent === 'hero') {
      if (window.matchMedia('(min-width: 1028px)').matches) {
        autoplay = this._toBoolean(root.dataset.splideAutoplayDesktop, false);
      } else {
        autoplay = false;
      }
    }
    const autoWidth = this._toBoolean(root.dataset.splideAutoWidth, false);
    const trimSpace = this._toBoolean(root.dataset.splideTrimSpace, true);
    const focus = root.dataset.splideFocus;
    const perPage = this._toNumber(root.dataset.splidePerPage, 1);
    const perMove = this._toNumber(root.dataset.splidePerMove, 1);
    const perPageMobile = this._toNumber(root.dataset.splidePerPageMobile, perPage);
    const perMoveMobile = this._toNumber(root.dataset.splidePerMoveMobile, perMove);
    const gap = root.dataset.splideGap || undefined;
    const gapMobile = root.dataset.splideGapMobile || gap;
    const autoWidthMobile = this._toBoolean(root.dataset.splideAutoWidthMobile, autoWidth);
    const trimSpaceMobile = this._toBoolean(root.dataset.splideTrimSpaceMobile, trimSpace);
    const fixedWidth = root.dataset.splideFixedWidth || undefined;
    const fixedWidthMobile = root.dataset.splideFixedWidthMobile || fixedWidth;
    const focusMobile = root.dataset.splideFocusMobile;
    const resolvedFocus = focus === undefined
      ? undefined
      : (focus === 'center' ? 'center' : this._toNumber(focus, 0));
    const resolvedFocusMobile = focusMobile === undefined
      ? resolvedFocus
      : (focusMobile === 'center' ? 'center' : this._toNumber(focusMobile, 0));
    const breakpoints = {
      989: {
        autoWidth: autoWidthMobile,
        trimSpace: trimSpaceMobile,
        fixedWidth: fixedWidthMobile,
        focus: resolvedFocusMobile,
        perPage: perPageMobile,
        perMove: perMoveMobile,
        gap: gapMobile,
      },
      749: {
        arrows: arrows && showArrowsMobile,
      },
    };

    return {
      type: root.dataset.splideType || 'fade',
      perPage,
      perMove,
      speed: this._toNumber(root.dataset.splideSpeed, 900),
      interval: this._toNumber(root.dataset.splideInterval, 5000),
      autoplay: autoplay ? 'pause' : false,
      pauseOnHover: this._toBoolean(root.dataset.splidePauseOnHover, true),
      pauseOnFocus: this._toBoolean(root.dataset.splidePauseOnFocus, true),
      rewind: this._toBoolean(root.dataset.splideRewind, true),
      arrows,
      pagination: counterType === 'dots',
      drag: this._toBoolean(root.dataset.splideDrag, true),
      keyboard: this._toBoolean(root.dataset.splideKeyboard, true) ? 'focused' : false,
      lazyLoad: false,
      autoWidth,
      trimSpace,
      focus: resolvedFocus,
      fixedWidth,
      gap,
      breakpoints,
    };
  }

  _updateSlideAccessibility(instance) {
    const slides = Array.from(
      instance.root.querySelectorAll('.splide__slide:not(.splide__slide--clone)')
    );

    slides.forEach((slide, index) => {
      slide.removeAttribute('role');
      slide.removeAttribute('aria-roledescription');
      slide.setAttribute('aria-label', `${index + 1} de ${slides.length}`);
    });
  }

  _updateCounter(instance) {
    const root = instance.root;
    const current = this._findCounterNode(root, '[data-splide-current]');
    const total = this._findCounterNode(root, '[data-splide-total]');
    const progress = this._findCounterNode(root, '[data-splide-progress]');
    const progressMode = root.dataset.splideProgressMode || 'slide';

    if (total) {
      total.textContent = String(instance.length);
    }

    if (current) {
      current.textContent = String(instance.index + 1);
    }

    if (progress) {
      const width = progressMode === 'viewport'
        ? this._getViewportProgress(instance)
        : progressMode === 'page'
          ? this._getPageProgress(instance)
        : this._getSlideProgress(instance);

      progress.style.width = `${width}%`;
    }
  }

  _getSlideProgress(instance) {
    if (instance.length <= 0) return 0;
    return ((instance.index + 1) / instance.length) * 100;
  }

  _getViewportProgress(instance) {
    const track = instance.root.querySelector('.splide__track');
    const list = instance.root.querySelector('.splide__list');

    if (!track || !list || list.scrollWidth <= 0) return 100;

    const maxOffset = Math.max(list.scrollWidth - track.clientWidth, 0);
    if (maxOffset <= 0) return 100;

    const position = Math.max(instance.Components.Move.getPosition(), -maxOffset);
    const viewedWidth = Math.min(Math.abs(position) + track.clientWidth, list.scrollWidth);

    return (viewedWidth / list.scrollWidth) * 100;
  }

  _getPageProgress(instance) {
    const endIndex = instance.Components.Controller.getEnd() + 1;
    if (endIndex <= 1) return 100;

    const pageIndex = Math.min(instance.index, endIndex - 1);
    return ((pageIndex + 1) / endIndex) * 100;
  }

  _findCounterNode(root, selector) {
    return root.querySelector(selector)
      || root.parentElement?.querySelector(selector)
      || root.closest('section, .shopify-section')?.querySelector(selector)
      || null;
  }

  _observeVideos(instance) {
    const root = instance.root;
    if (!root.querySelector('[data-splide-video]')) return;
    if (this._videoObservers.has(root)) return;
    if (typeof window.IntersectionObserver === 'undefined') return;

    const observer = new window.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this._syncSlideVideos(instance, true);
      }
    }, { rootMargin: '200px 0px' });

    this._videoObservers.set(root, observer);
    observer.observe(root);
  }

  _syncSlideVideos(instance, allowOutsideViewport = false) {
    if (!instance.root.querySelector('[data-splide-video]')) return;
    const slides = Array.from(
      instance.root.querySelectorAll('.splide__slide:not(.splide__slide--clone)')
    );
    const isInViewport = allowOutsideViewport || typeof window.IntersectionObserver === 'undefined' || this._isInViewport(instance.root);

    slides.forEach((slide) => {
      const isActive = slide.classList.contains('is-active');
      const videos = Array.from(slide.querySelectorAll('[data-splide-video]'));

      videos.forEach((video) => {
        if (isActive && isInViewport && this._isVisible(video)) {
          this._hydrateVideo(video);
          this._playVideo(video);
        } else {
          this._pauseVideo(video, true);
        }
      });
    });
  }

  _pauseAllVideos(root) {
    root.querySelectorAll('[data-splide-video]').forEach((video) => {
      this._pauseVideo(video, false);
    });
  }

  _hydrateVideo(video) {
    if (video.dataset.videoHydrated === 'true') return;

    const poster = video.dataset.poster;
    if (poster) {
      video.setAttribute('poster', poster);
    }

    const sources = video.querySelectorAll('source[data-src]');
    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    const directSrc = video.dataset.src;
    if (directSrc) {
      video.src = directSrc;
      video.removeAttribute('data-src');
    }

    video.load();
    video.dataset.videoHydrated = 'true';
  }

  _playVideo(video) {
    if (video.dataset.videoPlayPending === 'true') return;

    if (video.readyState < 2) {
      video.dataset.videoPlayPending = 'true';

      const onReady = () => {
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('canplay', onReady);
        video.dataset.videoPlayPending = 'false';
        this._playVideo(video);
      };

      video.addEventListener('loadeddata', onReady, { once: true });
      video.addEventListener('canplay', onReady, { once: true });
      return;
    }

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  _pauseVideo(video, resetTime = false) {
    video.dataset.videoPlayPending = 'false';
    video.pause();

    if (!resetTime) return;

    try {
      video.currentTime = 0;
    } catch (error) {
      /* noop */
    }
  }

  _isVisible(element) {
    return window.getComputedStyle(element).display !== 'none';
  }

  _isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  _toBoolean(value, fallback) {
    if (value === undefined) return fallback;
    return value === 'true';
  }

  _toNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }
}

window.SplideManager = new SplideManager();

